'use client'

// frontend/src/hooks/useShellCounts.ts
//
// The three counts on the rail: live procurements (Tender Explorer), decisions
// waiting (My Approvals) and questions handed to P2P (Supply Chain Desk).
//
// One fetch per route change, shared by every component that calls the hook
// (the rail and the drawer), through one small module-level store. A fetch
// that fails leaves its badge out; a count of zero is never shown. Nothing is
// polled here: the notification bell has its own 30 second poll.

import { useEffect, useSyncExternalStore } from 'react'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { apiClient } from '@/lib/api-client'

export interface ShellCounts {
  /** Live procurements, from the pipeline summary. */
  tenders?: number
  /** Decisions waiting on the viewer, from approvals/mine. */
  approvals?: number
  /** Questions handed to P2P and still open, from the Supply Chain desk. */
  supplyDesk?: number
}

const NO_COUNTS: ShellCounts = {}

let counts: ShellCounts = NO_COUNTS
let loadedFor: string | null = null
let requestId = 0
const listeners = new Set<() => void>()

function publish(next: ShellCounts) {
  counts = next
  listeners.forEach((listener) => listener())
}

function positive(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : undefined
}

async function load() {
  const mine = ++requestId
  const [pipeline, approvals, desk] = await Promise.allSettled([
    apiClient.get<{ summary?: { total?: number } }>('/api/tenders/pipeline'),
    apiClient.get<{ total?: number }>('/api/tenders/approvals/mine'),
    apiClient.get<{ handover_open?: number | null }>('/api/supply-chain/desk'),
  ])
  // A slower, older request must not overwrite a newer one.
  if (mine !== requestId) return
  publish({
    tenders: pipeline.status === 'fulfilled' ? positive(pipeline.value?.summary?.total) : undefined,
    approvals: approvals.status === 'fulfilled' ? positive(approvals.value?.total) : undefined,
    supplyDesk: desk.status === 'fulfilled' ? positive(desk.value?.handover_open) : undefined,
  })
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function useShellCounts(): ShellCounts {
  const pathname = usePathname()
  const { status } = useSession()

  useEffect(() => {
    if (status !== 'authenticated') return
    // Every caller asks on a route change; only the first one fetches.
    if (loadedFor === pathname) return
    loadedFor = pathname
    void load()
  }, [pathname, status])

  return useSyncExternalStore(
    subscribe,
    () => counts,
    () => NO_COUNTS
  )
}
